import { useEffect, useRef } from 'react';
import { Block, createComponent, If, IntrinsicProps, useResizeX } from 'react-commons';
import { App, Layout } from 'react-commons';

import { useAuthStore } from '@/lib/drupal/stores/auth';
import { CaptureClickEventContext } from '@/lib/util/captureClickEvent';
import AppHeader from '@/components/AppHeader';
import AppFooter from '@/components/AppFooter';

import style from './index.module.scss';
import PlaywireAside from '@/components/playwire/PlaywireAside';
import PlaywireUnit from '@/components/playwire/PlaywireUnit';
import PlaywireCornerVideo from '@/components/playwire/PlaywireCornerVideo';

interface ClassicGamePageLayoutProps extends IntrinsicProps {
  isFullscreen?: boolean
  hideLeaderboardAd?: boolean
  hideHeader?: boolean
  pageName?: string
  pageEventSettings?: any
}

const classStates = [
  'isFullscreen',
];

export default createComponent<ClassicGamePageLayoutProps>('ClassicGamePageLayout', { style, classStates }, function ClassicGamePageLayout ({ className }, props) {
  const contentElRef = useRef<HTMLDivElement>(null);
  const [ authState ] = useAuthStore();

  useResizeX((screenWidth) => {
    const el = contentElRef.current;

    const adColumnWidth = 332 + 16; // 332px ad + 16px margin
    const gamePlayerPodWidth = el.querySelector('.ClassicGamePlayerBlock > div').getBoundingClientRect().width;
    const containerPadding = 32; // 16px padding on each side

    const narrowBreakpoint = gamePlayerPodWidth + adColumnWidth + containerPadding;
    const wideBreakpoint = 1714;

    if (screenWidth >= wideBreakpoint) {
      el.classList.remove('--isNarrow');
      el.classList.add('--isWide');
    }
    else if (screenWidth >= narrowBreakpoint) {
      el.classList.remove('--isWide');
      el.classList.add('--isNarrow');
    }
    else {
      el.classList.remove('--isWide');
      el.classList.remove('--isNarrow');
    }
  });

  // React to changes in fullscreen state to request/dismiss fullscreen element
  useEffect(() => {
    if(props.isFullscreen) {
      document.body.classList.add('--fullScreen');

      if(typeof contentElRef.current?.requestFullscreen === 'function') {
        // requestFullscreen() will not work until the user has interacted with
        // an element on the page. This is a documented behaviour:
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/requestFullscreen#security
        //
        // In this case, the best we can do is a pseudo-fullscreen state where
        // we hide all extraneous website UI.
        contentElRef.current.requestFullscreen().catch((error) => {
          // If we were denied fullscreen access, catch and discard the error
          return error;
        });
      }
    } else {
      document.body.classList.remove('--fullScreen');

      if(document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  }, [ props.isFullscreen ]);

  return (
    <CaptureClickEventContext.Provider value={{
      page: props.pageName,
      settings: props.pageEventSettings || {},
    }}>
      <Layout className={className}>
        {
          If(!props.hideHeader, () => (
            <App.Header>
              <AppHeader />
            </App.Header>
          )).EndIf()
        }

        <App.Main>
          <div
            ref={contentElRef} 
            className='Flex Flex--justifyCenter ClassicGamePageLayout__Content --isWide'
          >
            <aside>
              {
                If(!authState.user?.isPremiumUser, () => (
                  <PlaywireAside stickToLeft>
                    <PlaywireUnit unit='med_rect_btf' />
                    <PlaywireUnit unit='med_rect_btf' />
                  </PlaywireAside>
                )).EndIf()
              }
            </aside>

            <div>
              {props.children}
              {
                If(!authState.user?.isPremiumUser, () => (
                  <PlaywireCornerVideo />
                )).EndIf()
              }
            </div>

            <aside>
              {
                If(!authState.user?.isPremiumUser, () => (
                  <PlaywireAside useAdFallback useVideoPlaceholder>
                    <PlaywireUnit unit='med_rect_atf' />
                  </PlaywireAside>
                )).EndIf()
              }
            </aside>
          </div>
        </App.Main>

        {
          If(!props.isFullscreen, () => (
            <App.Footer>
              <AppFooter />
            </App.Footer>
          )).EndIf()
        }
        
      </Layout>
    </CaptureClickEventContext.Provider>
  );
});

export const ClassicGamePageInner = createComponent('ClassicGamePageInner', { style }, function ClassicGamePageInner ({ className }, props) {
  return (
    <div className={className}>
      {props.children}
    </div>
  );
});

export const ClassicGamePlayerBlock = createComponent('ClassicGamePlayerBlock', { style }, function ClassicGamePlayerBlock ({ className }, props) {
  return (
    <Block className={className}>
      {props.children}
    </Block>
  );
});